import { useMemo, useCallback } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { emptyArray } from 'ui-components/default-props/empty';

import { messages } from './messages';
import { REFRESH_SELECTED_ORDERS_CLICK } from '../../../constants/analytics-events';
import { MarketplaceIds } from '../../../enums/marketplace-ids';
import { Segment } from '../../../services/analytics-services/segment';
import Global from '../../../store/global-selectors';
import { getActualSalesOrderIds } from '../../../store/orders/orders.helpers';
import { refreshSelectedOrders } from '../../../store/seller/seller.actions';
import { EventSourceStrings } from '../../../tracking/tracking-constants';
import type { SalesOrder } from '../../../types/entities';

type UseRefreshSelectedOrdersResult = {
  actionHandler: () => void;
  nameDescriptor: MessageDescriptor;
  isDisabled: boolean;
  disabledReason: MessageDescriptor | null;
};

// Marketplaces that support order refresh via their APIs.
// Add marketplaces here as refresh support is validated.
// TODO: move to integrations-registry
const REFRESHABLE_MARKETPLACE_IDS: number[] = [MarketplaceIds.DummyMarketplace, MarketplaceIds.OAuthDummy, MarketplaceIds.Shopify];

const areAllOrdersRefreshable = (salesOrders: SalesOrder[] | null | undefined): boolean => {
  if (!salesOrders || salesOrders.length === 0) {
    return false;
  }
  return salesOrders.every((salesOrder) => {
    const marketplaceId = salesOrder?.store?.marketplaceId;
    return marketplaceId != null && REFRESHABLE_MARKETPLACE_IDS.includes(Number(marketplaceId));
  });
};

export const useRefreshSelectedOrders = (): UseRefreshSelectedOrdersResult => {
  const dispatch = useDispatch();
  const selectedOrderIdsRaw = useSelector(Global.selectSelectedOrdersIds) as number[] | null | undefined;
  const selectedOrderIds = useMemo<number[]>(() => selectedOrderIdsRaw ?? (emptyArray as number[]), [selectedOrderIdsRaw]);

  const actualSalesOrderIds = useSelector((state) => getActualSalesOrderIds(state, selectedOrderIds));

  const salesOrders = useSelector((state) =>
    Global.selectEntitiesSalesOrdersByIds(state, { salesOrderIds: actualSalesOrderIds }),
  ) as SalesOrder[];

  const allRefreshable = useMemo(() => areAllOrdersRefreshable(salesOrders), [salesOrders]);

  const hasNoOrdersSelected = selectedOrderIds.length === 0;
  const isDisabled = hasNoOrdersSelected || !allRefreshable;

  const disabledReasonMessageDescriptor = useMemo<MessageDescriptor | null>(() => {
    if (hasNoOrdersSelected) {
      return null;
    }
    if (!allRefreshable) {
      return messages.refreshSelectedOrdersUnsupportedMarketplace;
    }
    return null;
  }, [hasNoOrdersSelected, allRefreshable]);

  const actionHandler = useCallback(() => {
    Segment.trackEvent(REFRESH_SELECTED_ORDERS_CLICK, {
      source: EventSourceStrings.ORDER_GRID_HEADER_OTHER_ACTIONS_DROPDOWN,
      orderCount: selectedOrderIds.length,
    });
    dispatch(refreshSelectedOrders(selectedOrderIds));
  }, [dispatch, selectedOrderIds]);

  return useMemo(
    () => ({
      actionHandler,
      nameDescriptor: messages.orderGridOtherActionsRefreshSelectedOrders,
      isDisabled,
      disabledReason: disabledReasonMessageDescriptor,
    }),
    [actionHandler, isDisabled, disabledReasonMessageDescriptor],
  );
};
